import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Home, ShoppingBag, Wine, ChevronRight, CreditCard, UserPlus, Loader2, Euro } from "lucide-react";
import api, { API, TOKEN_KEY } from "@/lib/api";
import { PREP_STATUS, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);
const prettyDate = (iso) =>
  new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

function ActionCard({ testId, icon: Icon, count, label, tint, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-[#E5DEC9] overflow-hidden shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-4 p-4 text-left">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <p className="font-serif text-3xl font-bold text-[#110F0F] leading-none">{count}</p>
          <p className="text-sm text-[#6E675F] mt-0.5">{label}</p>
        </div>
        <ChevronRight className={`w-5 h-5 text-[#6E675F] transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="border-t border-[#E5DEC9] divide-y divide-[#F5EFEB]">{children}</div>}
    </div>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [news, setNews] = useState([]);
  const [rev, setRev] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [t, subs, n, r] = await Promise.all([
      api.get("/admin/today", { params: { day: todayStr() } }),
      api.get("/admin/subscribers", { params: { filter: "en_attente" } }),
      api.get("/admin/new-subscribers"),
      api.get("/admin/revenue"),
    ]);
    setData(t.data);
    setPending(subs.data);
    setNews(n.data);
    setRev(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const markPrep = async (orderId, status) => {
    await api.patch(`/admin/orders/${orderId}`, { preparationStatus: status });
    toast.success("Statut mis à jour");
    load();
  };

  const exportRevenue = () => {
    const token = localStorage.getItem(TOKEN_KEY);
    fetch(`${API}/admin/revenue/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `encaissements_${new Date().toISOString().slice(0, 7)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export impossible. Vérifiez votre connexion."));
  };

  if (loading)
    return <div className="flex justify-center pt-24"><Loader2 className="w-7 h-7 animate-spin text-[#581825]" /></div>;

  const Row = ({ item, actions }) => (
    <div className="flex items-center gap-3 p-3">
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#110F0F] truncate">{item.name}</p>
        <p className="text-xs text-[#6E675F]">{item.planLabel} · {item.bottles} bouteilles{item.address ? ` · ${item.address}` : ""}</p>
      </div>
      {actions}
    </div>
  );

  return (
    <div className="px-4 pt-6 space-y-5">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#6E675F]">Aujourd'hui</p>
        <h1 className="font-serif text-3xl font-bold text-[#581825] capitalize">{prettyDate(data.date)}</h1>
        <p className="text-sm text-[#6E675F] mt-1">
          {data.totalBottles} bouteille{data.totalBottles > 1 ? "s" : ""} à préparer au total
        </p>
      </div>

      {rev && (
        <div className="space-y-2">
        <div data-testid="revenue-widget" className="rounded-2xl bg-[#581825] text-[#FDFBF7] p-4 flex items-stretch">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-1.5 text-[#C5A059] text-xs uppercase tracking-wider">
              <Euro className="w-3.5 h-3.5" /> Encaissé aujourd'hui
            </div>
            <p className="font-serif text-3xl font-bold mt-1" data-testid="revenue-day">{rev.day.toFixed(2)} €</p>
            <p className="text-xs text-[#F5EFEB]/60">{rev.dayCount} paiement{rev.dayCount > 1 ? "s" : ""}</p>
          </div>
          <div className="w-px bg-white/15" />
          <div className="flex-1 pl-4">
            <div className="text-[#C5A059] text-xs uppercase tracking-wider">Ce mois-ci</div>
            <p className="font-serif text-3xl font-bold mt-1" data-testid="revenue-month">{rev.month.toFixed(2)} €</p>
            <p className="text-xs text-[#F5EFEB]/60">{rev.monthCount} paiement{rev.monthCount > 1 ? "s" : ""}</p>
          </div>
        </div>
        <button data-testid="btn-export-revenue" onClick={exportRevenue}
          className="w-full text-center text-xs font-semibold text-[#581825] bg-[#F5EFEB] border border-[#E5DEC9] rounded-xl py-2.5 hover:bg-[#EFE7DF] transition-colors">
          ⬇︎ Exporter le CSV des encaissements du mois
        </button>
        </div>
      )}

      <div className="space-y-3">
        <ActionCard testId="today-card-to-prepare" icon={Package} count={data.toPrepare.length}
          label="commandes à préparer" tint="bg-amber-100 text-amber-800">
          {data.toPrepare.length === 0 && <p className="p-4 text-sm text-[#6E675F]">Rien à préparer.</p>}
          {data.toPrepare.map((i) => (
            <Row key={i.orderId} item={i} actions={
              <Button size="sm" data-testid="btn-mark-pret" onClick={() => markPrep(i.orderId, "pret")}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8">Marquer prêt</Button>
            } />
          ))}
        </ActionCard>

        <ActionCard testId="today-card-deliveries" icon={Home} count={data.deliveries.length}
          label="livraisons" tint="bg-blue-100 text-blue-800">
          {data.deliveries.length === 0 && <p className="p-4 text-sm text-[#6E675F]">Aucune livraison.</p>}
          {data.deliveries.map((i) => (
            <Row key={i.orderId} item={i} actions={
              <div className="flex items-center gap-2">
                <StatusBadge type="prep" value={i.preparationStatus} />
                <Button size="sm" onClick={() => markPrep(i.orderId, "livre")}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8">Livré</Button>
              </div>
            } />
          ))}
        </ActionCard>

        <ActionCard testId="today-card-pickups" icon={ShoppingBag} count={data.pickups.length}
          label="retraits magasin" tint="bg-purple-100 text-purple-800">
          {data.pickups.length === 0 && <p className="p-4 text-sm text-[#6E675F]">Aucun retrait.</p>}
          {data.pickups.map((i) => (
            <Row key={i.orderId} item={i} actions={
              <div className="flex items-center gap-2">
                <StatusBadge type="prep" value={i.preparationStatus} />
                <Button size="sm" onClick={() => markPrep(i.orderId, "retire")}
                  className="bg-purple-600 hover:bg-purple-700 text-white h-8">Retiré</Button>
              </div>
            } />
          ))}
        </ActionCard>
      </div>

      {/* Pending payments */}
      <section>
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-2">
          <CreditCard className="w-5 h-5" /> Paiements en attente ({pending.length})
        </h2>
        <div className="rounded-2xl bg-white border border-[#E5DEC9] divide-y divide-[#F5EFEB]">
          {pending.length === 0 && <p className="p-4 text-sm text-[#6E675F]">Aucun paiement en attente.</p>}
          {pending.slice(0, 6).map((s) => (
            <button key={s.id} onClick={() => navigate(`/admin/clients/${s.id}`)}
              className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F5EFEB]">
              <div className="flex-1">
                <p className="font-semibold text-[#110F0F]">{s.customer.firstName} {s.customer.lastName}</p>
                <p className="text-xs text-[#6E675F]">{s.planLabel} · {s.monthlyPrice} €/mois</p>
              </div>
              <StatusBadge value={s.status} />
              <ChevronRight className="w-4 h-4 text-[#6E675F]" />
            </button>
          ))}
        </div>
      </section>

      {/* New subscribers */}
      <section>
        <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#581825] mb-2">
          <UserPlus className="w-5 h-5" /> Nouveaux abonnés ({news.length})
        </h2>
        <div className="rounded-2xl bg-white border border-[#E5DEC9] divide-y divide-[#F5EFEB]">
          {news.length === 0 && <p className="p-4 text-sm text-[#6E675F]">Aucun nouvel abonné.</p>}
          {news.slice(0, 6).map((s) => (
            <button key={s.id} onClick={() => navigate(`/admin/clients/${s.id}`)}
              className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F5EFEB]">
              <Wine className="w-4 h-4 text-[#581825]" />
              <div className="flex-1">
                <p className="font-semibold text-[#110F0F]">{s.customer.firstName} {s.customer.lastName}</p>
                <p className="text-xs text-[#6E675F]">{s.customer.phone} · {s.deliveryMethod === "livraison" ? "Livraison" : "Retrait"}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-[#6E675F]" />
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
